<template>
  <el-tree
    ref="treeRef"
    :key="random"
    :data="data"
    show-checkbox
    disabeld
    :node-key="nodeKey"
    accordion
    :default-checked-keys="defaultCheckedKeys"
    :props="defaultProps"
    :filter-node-method="filterNode"
    @change="handleCurrentChange"
  >
    <template #default="{ node, data: v }">
      <span class="custom-tree-node truncate">
        <span>{{ node?.label?.title || v?.invoiceSubName }}</span>
      </span>
    </template>
  </el-tree>
</template>

<script setup lang="ts">
import { IRoleMenuItem } from '../../../../types/common';
import { formatTreeData, getTreeDataCount } from '@core/tools';
import { ElTree } from 'element-plus';
import { PropType, ref, watch } from 'vue';

const emit = defineEmits<{change: [value: 'unchecked' | 'checked' | 'indeterminate']}>();

const props = defineProps({
  /** 菜单数据 */
  data: {
    type: Object as PropType<IRoleMenuItem[]>,
    default: () => {
      return [] as IRoleMenuItem[];
    },
  },
  /** 选中节点ids */
  checkedData: {
    type: Object as PropType<number[]>,
    default: () => {
      return [] as number[];
    },
  },
  /** 是否禁用 */
  disabled: {
    type: Boolean,
    default: true,
  },
  /** 节点唯一标识，默认为'menuId' */
  nodeKey: {
    type: String,
    default: 'menuId',
  },
  isCheckedDataFormat: {
    type: Boolean,
    default: true,
  },
});

const defaultProps = {
  children: 'children',
  label: 'meta',
  disabled: () => props.disabled,
};

const treeRef = ref<InstanceType<typeof ElTree>>();
const random = ref();
const defaultCheckedKeys = ref<number[]>([]);

function filterNode(value: string, data) {
  if (!value) return true;
  
  return data?.invoiceSubName?.includes(value) || data?.meta?.title?.includes(value);
}

function handleCurrentChange() {
  const checkedKeys = treeRef.value?.getCheckedKeys() as number[];
  const halfCheckedKeys = treeRef.value?.getHalfCheckedKeys() as number[];
  
  const checkedStatus = checkedKeys.length === 0 && halfCheckedKeys.length === 0
    ? 'unchecked' : 
    halfCheckedKeys.length === 0 && checkedKeys.length === getTreeDataCount(props.data)
      ? 'checked' : 
      'indeterminate';

  emit('change', checkedStatus);
}

watch(
  [
    () => props.data,
    () => props.checkedData,
  ],
  () => {
    if (props.data) {
      if (props.isCheckedDataFormat) {
        defaultCheckedKeys.value = !props.checkedData || props.checkedData.length === 0 ? [] : formatTreeData(props.checkedData, props.data);
      } else {
        defaultCheckedKeys.value = !props.checkedData || props.checkedData.length === 0 ? [] : props.checkedData;
      }
    }
    random.value = Math.random();
  },
  { immediate: true },
);

defineExpose({
  /** 获取选中子节点ids */
  getOnlyCheckedKeys: () => [...treeRef.value?.getCheckedKeys()] as number[],
  /** 获取所有选中节点ids */
  getCheckedKeys: () => [...treeRef.value?.getHalfCheckedKeys(), ...treeRef.value?.getCheckedKeys()] as number[],
  /* 获取选中节点数据 */
  getCheckedNodes: () => [...treeRef.value?.getCheckedNodes().filter((v) => v?.level !== 0)],
  /* 获取treeRef */
  getTreeRef: () => treeRef.value,
});
</script>

<style lang="scss" scoped>
:deep(.el-tree-node__content){
  @apply my-1;
}
</style>